import { useSyncExternalStore } from 'react';

/**
 * SSR-safe `matchMedia` subscription. Returns the current match state and
 * re-renders when the media query result flips.
 *
 * @example
 * const isWide = useMediaQuery('(min-width: 1024px)');
 */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (callback) => {
      if (!hasMatchMedia()) return () => {};
      const mql = window.matchMedia(query);
      mql.addEventListener('change', callback);
      return () => mql.removeEventListener('change', callback);
    },
    () => {
      if (!hasMatchMedia()) return false;
      return window.matchMedia(query).matches;
    },
    () => false,
  );
}

/**
 * `matchMedia` is missing in a non-browser render (SSR) and in some embedded webviews / test
 * environments, where calling it throws. Same guard `theme-store` already uses; an unmatched query
 * reads as `false`, which keeps every caller on its narrow/default branch instead of crashing.
 */
function hasMatchMedia(): boolean {
  return (
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  );
}

export type Breakpoint = 'mobile' | 'tablet' | 'desktop' | 'wide';

/**
 * Returns the current breakpoint bucket. Used by AppShell to swap chrome
 * (sidebar collapse, sheet drawer, bottom tab bar) without re-mounting routes.
 *
 * Breakpoints align with Tailwind v4 defaults:
 * - mobile  : < 768
 * - tablet  : 768  – 1024
 * - desktop : 1024 – 1280
 * - wide    : ≥ 1280
 */
export function useBreakpoint(): Breakpoint {
  const isWide = useMediaQuery('(min-width: 1280px)');
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const isTablet = useMediaQuery('(min-width: 768px)');
  if (isWide) return 'wide';
  if (isDesktop) return 'desktop';
  if (isTablet) return 'tablet';
  return 'mobile';
}
